import React, { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';

import { schemas } from '@recurrency/core-api-schema';
import { TenantProductName } from '@recurrency/core-api-schema/dist/common/enums';
import { TransferOrderLineDTO } from '@recurrency/core-api-schema/dist/transferOrderLines/getTransferOrderLines';
import { ColumnType } from 'antd/lib/table';

import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { DateRangePicker } from 'components/DatePicker';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpacer } from 'components/FlexSpacer';
import { ResultCount } from 'components/ResultCount';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { DateFilter, formatMomentDateToSqlDate, getDefaultDateFilter } from 'utils/date';
import { shouldShowProduct } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { asKeyOf, sortableDateColumn, sortableNumberColumn, sortableStringColumn } from 'utils/tables';

export function TransferOrderLinesTable({
  itemId,
  locationId,
  direction,
}: {
  itemId: string;
  locationId: string;
  direction?: 'inbound' | 'outbound';
}) {
  const { activeTenant } = useGlobalApp();

  const hasDfpaPurchasingAutomationProduct = shouldShowProduct(
    activeTenant,
    TenantProductName.DfpaPurchasingAutomation,
  );
  const [filteredDate, setFilteredDate] = useState<DateFilter>(getDefaultDateFilter);

  const tableProps = useCoreApiTableProps({
    schema: schemas.transferOrderLines.getTransferOrderLines,
    queryParams: {
      filter: {
        itemId,
        locationId,
        transferDateFrom: formatMomentDateToSqlDate(filteredDate.from),
        transferDateTo: formatMomentDateToSqlDate(filteredDate.to),
      },
    },
  });

  const { setPage } = tableProps;
  useEffect(() => {
    setPage(1);
  }, [filteredDate, setPage]);

  if (direction) {
    tableProps.items = tableProps.items.filter((line) =>
      direction === 'inbound' ? line.toLocationId === locationId : line.fromLocationId === locationId,
    );
    tableProps.totalCount = tableProps.items.length;
  }

  const columns: ColumnType<TransferOrderLineDTO>[] = [
    sortableStringColumn({
      title: 'Transfer No',
      dataIndex: asKeyOf<TransferOrderLineDTO>('transferNumber'),
      render: (id: string) =>
        hasDfpaPurchasingAutomationProduct ? <Link to={routes.purchasing.transferOrderDetails(id)}>{id}</Link> : id,
      sorter: true,
    }),
    sortableDateColumn({
      title: 'Transfer Date',
      dataIndex: asKeyOf<TransferOrderLineDTO>('transferDate'),
      sorter: true,
      defaultSortOrder: 'descend',
    }),
    sortableDateColumn({
      title: 'Shipped Date',
      dataIndex: asKeyOf<TransferOrderLineDTO>('shippedDate'),
      sorter: true,
    }),
    sortableDateColumn({
      title: 'Received Date',
      dataIndex: asKeyOf<TransferOrderLineDTO>('receivedDate'),
      sorter: true,
    }),
    sortableStringColumn({
      title: 'Source',
      dataIndex: asKeyOf<TransferOrderLineDTO>('fromLocationId'),
      render: (_: string, record: TransferOrderLineDTO) =>
        `${record.fromLocationId}: ${record.fromLocationName ?? ''}`.trim(),
      sorter: true,
    }),
    sortableStringColumn({
      title: 'Destination',
      dataIndex: asKeyOf<TransferOrderLineDTO>('toLocationId'),
      render: (_: string, record: TransferOrderLineDTO) =>
        `${record.toLocationId}: ${record.toLocationName ?? ''}`.trim(),
      sorter: true,
    }),
    sortableNumberColumn({
      title: 'Qty to Transfer',
      dataIndex: asKeyOf<TransferOrderLineDTO>('quantityToTransfer'),
      sorter: true,
    }),
    sortableStringColumn({
      title: 'UOM',
      dataIndex: asKeyOf<TransferOrderLineDTO>('unitOfMeasure'),
      sorter: true,
    }),
  ];

  return (
    <>
      <FilterBarBox>
        <FlexSpacer />
        <DateRangePicker
          value={[filteredDate.from, filteredDate.to]}
          onChange={(values) => setFilteredDate(values ? { from: values[0], to: values[1] } : getDefaultDateFilter())}
          format="MM/DD/YYYY"
        />
        <ResultCount count={tableProps.totalCount} />
      </FilterBarBox>
      <AsyncTable tableProps={tableProps} columns={columns} />
    </>
  );
}
